import React, { useState, useRef, useEffect } from 'react';
import AdDisplay from './AdDisplay';
import { Box, IconButton } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Sticky Popup Ad Component
 * Draggable popup ad that can be moved anywhere on the screen
 */
const StickyPopupAd = ({ positionId }) => {
  const [position, setPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isClosed, setIsClosed] = useState(false);
  const boxRef = useRef(null);

  // Initialize position on mount (bottom right corner)
  useEffect(() => {
    if (position.x === null && position.y === null) {
      const updateInitialPosition = () => {
        if (boxRef.current) {
          const rect = boxRef.current.getBoundingClientRect();
          const windowWidth = window.innerWidth;
          const windowHeight = window.innerHeight;
          setPosition({
            x: windowWidth - rect.width - 20,
            y: windowHeight - rect.height - 20,
          });
        }
      };
      updateInitialPosition();
      window.addEventListener('resize', updateInitialPosition);
      return () => window.removeEventListener('resize', updateInitialPosition);
    }
  }, [position]);

  const handleMouseDown = (e) => {
    if (boxRef.current) {
      const rect = boxRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging && position.x !== null && position.y !== null) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        // Constrain to viewport bounds
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const boxWidth = boxRef.current?.offsetWidth || 300;
        const boxHeight = boxRef.current?.offsetHeight || 250;
        
        const constrainedX = Math.max(0, Math.min(newX, windowWidth - boxWidth));
        const constrainedY = Math.max(0, Math.min(newY, windowHeight - boxHeight));
        
        setPosition({ x: constrainedX, y: constrainedY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset, position]);

  const handleClose = (e) => {
    e.stopPropagation();
    setIsClosed(true);
  };

  if (!positionId || isClosed) {
    return null;
  }

  return (
    <Box
      ref={boxRef}
      sx={{
        position: 'fixed',
        left: position.x !== null ? `${position.x}px` : 'auto',
        top: position.y !== null ? `${position.y}px` : 'auto',
        bottom: position.x === null ? 20 : 'auto',
        right: position.x === null ? 20 : 'auto',
        width: '300px',
        height: '250px',
        zIndex: 9998,
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        overflow: 'hidden',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        '@media (max-width: 768px)': {
          width: '250px',
          height: '200px',
          bottom: position.x === null ? 10 : 'auto',
          right: position.x === null ? 10 : 'auto',
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          width: '28px',
          height: '28px',
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          zIndex: 2,
          padding: '4px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <CloseIcon
          sx={{
            color: 'rgba(0, 0, 0, 0.6)',
            fontSize: '18px',
          }}
        />
      </IconButton>

      {/* Drag Handle */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: 'absolute',
          top: 8,
          right: 40,
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          borderRadius: '4px',
          cursor: isDragging ? 'grabbing' : 'grab',
          zIndex: 1,
          paddingRight: '4px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <DragIndicatorIcon
          sx={{
            color: 'rgba(0, 0, 0, 0.5)',
            fontSize: '16px',
          }}
        />
      </Box>
      <AdDisplay
        positionId={positionId}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          pointerEvents: 'none', // Prevent ad content from interfering with drag
        }}
      />
    </Box>
  );
};

export default StickyPopupAd;

